/* eslint-disable arrow-body-style */
import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Difference} from "../models/difference";
import {ProjectStore} from "./project-store-mobx";
import {ProjectPerimeter, ProjectPerimeterProps} from "../models/project-perimeter";
import {JournalStore} from "./journal-store-mobx";
import {MapState} from "../core/map-state";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import {ItemType} from "../models/item-type";

export interface ProjectPerimeterStoreProps {
    layerConfig: ApiLayerConfiguration;
    projectStore?: ProjectStore;
    journalStore?: JournalStore;
    mapStore: MapState;
}

export class ProjectPerimeterStore extends AbstractStore {

    projectStore: ProjectStore | undefined;
    journalStore: JournalStore | undefined;
    mapStore: MapState;

    perimeterProposal: __esri.Graphic | null;

    /**
     * Used to remember unsaved project perimeters.
     */
    proposal: ProjectPerimeter | __esri.Graphic | null;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProjectPerimeter>();

    /**
     * The representation of the API layer, but loaded in the map view
     */
    viewLayer: EsriFeatureLayer;

    constructor(props: ProjectPerimeterStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;
        this.projectStore = props.projectStore;
        this.mapStore = props.mapStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            perimeterProposal: observable,
            proposal: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            visibleItems: computed,

            addItem: action,
            removeItem: action,
            clearProposal: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProjectPerimeter[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(ProjectPerimeter.sortByName);
        return [ ...filtered];
    }

    clearProposal(): void {
        this.proposal = null;
    }

    newPerimeter(geometry?: __esri.Geometry): Promise<__esri.Graphic> {
        return new Promise((resolve, reject) => {
            if (!this.perimeterProposal && !geometry) {
                reject();
                return;
            }
            autorun((reaction: any) => {
                if (!this.isLoaded || !this.mapStore.map) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                const perimeter = this.createFeature(geometry || this.perimeterProposal?.geometry);
                if (!perimeter) {
                    reject();
                    return;
                }

                // use layer from map view not layer loaded in the background
                if (!this.viewLayer) {
                    reject();
                    return;
                }
                perimeter.layer = this.viewLayer;

                resolve(perimeter);
            });
        });
    }

    /**
     * Request all perimeters from server and store in local store
     *
     * @param projectId
     * @param guids (Optional) IDs of perimeters
     */
    loadItems(projectId?: number, guids?: string[]): Promise<ProjectPerimeter[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectId, guids).then((perimeters: ProjectPerimeter[]) => {
                    if (perimeters) {
                        // do only save to mobx when not requested with specified ids or contacts
                        const map = new Map<string, ProjectPerimeter>();
                        perimeters.forEach((a: ProjectPerimeter) => {
                            map.set(a.guid, a);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(perimeters);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting perimeters");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param projectId
     * @param user
     * @param geometry
     */
    newItem(projectId: number | undefined, user: string, geometry: __esri.Geometry | undefined): Promise<ProjectPerimeter | null> {
        return new Promise((resolve, reject) => {

            if (!projectId) {
                reject();
            }

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // generate random perimeter id
                const randomIds = new Uint8Array(1);
                self.crypto.getRandomValues(randomIds);

                const perimeter = new ProjectPerimeter({
                    creator: user,
                    projectId,
                    guid: crypto.randomUUID()
                });

                // TODO: find out why the feature template prototype does not return attributes
                perimeter.feature = this.createFeature(null, perimeter);

                if (geometry) {
                    perimeter.feature.geometry = geometry;
                }

                // workaround: add missing prototype attributes
                ProjectPerimeter.prototypeAttributes.forEach((attrName) => {
                    if (!perimeter.feature.attributes[attrName]) {
                        perimeter.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(perimeter);
                this.setSelectedItem(perimeter);

                resolve(perimeter);
            });
        });
    }

    /**
     * Save to the server
     *
     * @param perimeter
     * @param updated
     * @param user
     */
    save(perimeter: ProjectPerimeter | null,
         updated: any,
         user: string): Promise<ProjectPerimeter | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!perimeter?.guid) {
                    reject("Perimeter GUID undefined when saving edits.");
                    return;
                }

                if (perimeter.isNewRecord) {
                    // New project --> create feature
                    // persist geometry
                    const geometry = perimeter.feature?.geometry;
                    perimeter.feature = this.createFeature(null, perimeter);
                    if (geometry) {
                        perimeter.feature.geometry = geometry;
                    }

                    // log project creation in journal
                    const idDiff = new Difference({p: "guid", n: perimeter.guid});
                    this.journalStore?.journalizeItem(
                        perimeter,
                        "project-perimeter",
                        "add",
                        user,
                        [idDiff],
                        "Neuer Perimeter"
                    );
                }

                if(!perimeter.isNewRecord && updated) {
                    // log changes to journal
                    const diff = this.compareAttributes(perimeter, updated, ProjectPerimeter.comparableAttributes);
                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        perimeter,
                        "project",
                        "update",
                        user,
                        diff,
                        "Perimeter geändert"
                    );
                }

                const feature = perimeter?.feature;
                if (!feature || !feature.geometry) {
                    reject(`Feature or geometry undefined for perimeter ${perimeter.guid}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = ProjectPerimeter.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for perimeter ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = perimeter.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(perimeter, attrs);
                    if (perimeter.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        perimeter.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(perimeter);
                }, (error) => {
                    console.error("Error saving perimeter", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete from server
     *
     * @param perimeter
     * @param user
     */
    delete(perimeter: ProjectPerimeter | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!perimeter?.guid) {
                    reject("Perimeter GUID undefined when saving edits.");
                    return;
                }

                if (perimeter.isNewRecord) {
                    reject("This perimeter is not saved. Does not have to be deleted.");
                    return;
                }

                const feature = perimeter?.feature;
                if (!feature) {
                    reject(`Feature undefined for perimeter ${perimeter.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {

                    this.resetSelectedItem();
                    this.removeItem(perimeter);

                    // log deletion in journal
                    const idDiff = new Difference({p: "guid", o: perimeter.guid});
                    this.journalStore?.journalizeItem(
                        perimeter,
                        "project-perimeter",
                        "delete",
                        user,
                        [idDiff],
                        "Perimeter gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectId?: number,
                  perimeterGuids?: string[],
                  geometry?: __esri.Geometry,
                  spatialRelationship?: "intersects"
                      | "contains"
                      | "crosses"
                      | "disjoint"
                      | "envelope-intersects"
                      | "index-intersects"
                      | "overlaps"
                      | "touches"
                      | "within"
                      | "relation"): Promise<ProjectPerimeter[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                if (projectId !== undefined) {
                    queryParams.where += ` AND PROJ_ID = ${projectId}`;
                }
                // load all features if no project ID is specified
                if (perimeterGuids !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND GUID IN ('${perimeterGuids.join("','")}')`;
                }

                if (geometry) {
                    queryParams.geometry = geometry;
                }
                if (spatialRelationship) {
                    queryParams.spatialRelationship = spatialRelationship;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const activities = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(activities);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying perimeter + ${error}`);
                });
            });
        });
    }

    /**
     * WORKAROUND, because this.layer.templates[0] is undefined.
     * TODO: delete this function as soon as 'this.layer.templates[0]' in AbstractStore.createFeature is not undefined anymore
     * OVERRIDE: layer templates not working for feature layers (because using domain and unique value renderer?)
     * Create a new feature of the specified type
     * Uses layers FeatureTemplate prototype
     *
     * @see: https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-FeatureTemplate.html#prototype
     * @param geometry
     * @param props
     */
    createFeature(geometry?: __esri.Geometry | null, props?: ItemType): __esri.Graphic {
        if (!this.isLoaded) {
            throw new Error("Layer not found creating feature.");
        }

        // WORKAROUND
        const attributes = {};
        ProjectPerimeter.databaseFieldNames.forEach((fieldName) => {
            attributes[fieldName] = null;
        });
        const feature = new Graphic({
            attributes,  // do clone! otherwise attributes are referenced and reused
            layer: this.layer // store reference to layer. is needed when editing this feature in editor
        });

        if (geometry) {
            feature.geometry = geometry.clone();
        }
        if (props) {
            // Loop through updated attributes and assign
            // the updated values to feature attributes.
            const json: any = props.toJSON();
            if (!json) {
                throw new Error("Error converting object to JSON creating feature.");
            }
            Object.keys(json).forEach((name) => {
                feature.attributes[name] = json[name];
            });
        }

        return feature;
    }

    /**
     * Override loadLayer to also load view layer
     * @param layerConfig
     * @protected
     */
    protected loadLayer(layerConfig: ApiLayerConfiguration): Promise<EsriFeatureLayer> {
        return new Promise((resolve, reject) => {
            try {
                // wait for map view > find the instance of this layer loaded to the map
                // the perimeter layer is loaded twice. Once for the API, once to show in the map
                autorun((reaction: any) => {
                    if (!this.mapStore?.map) {
                        return;
                    }
                    reaction.dispose(); // stop autorun

                    // find same layer in map view
                    this.viewLayer = this.mapStore.map.layers.flatten((item: __esri.Layer) => {
                        return (item as __esri.GroupLayer)?.layers;
                    }).find((layer: __esri.FeatureLayer) => {
                        return layer.portalItem?.id === this.layer.portalItem.id &&
                            layer.layerId === this.layer.layerId;
                    }) as __esri.FeatureLayer;
                });

                const featureLayer: EsriFeatureLayer =
                    new EsriFeatureLayer(layerConfig as __esri.FeatureLayerProperties);

                featureLayer.load().then(() => {
                    this.layer = featureLayer;
                    resolve(featureLayer);
                }, () => {
                    reject(layerConfig.objectType);
                });
            } catch (errors) {
                console.error(errors);
                reject(layerConfig.objectType);
            }
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProjectPerimeter[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const perimeters: ProjectPerimeter[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProjectPerimeter);
            const a = new ProjectPerimeter(json as ProjectPerimeterProps);
            a.feature = feature;
            perimeters.push(a);
        });
        return perimeters;
    }

}
